import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCookies } from 'react-cookie';
import { parse } from 'querystring';
import { Page } from '../../components/page';
import { formatCurrency, PaymentInformationSection } from '../../components';
import { LoadingProgress } from '../../layout';
import {
  fromAccounts,
  fromBillingInformation,
  fromOrder,
  fromPaymentMethod,
  fromSubscription,
  useAppDispatch,
} from '../../store';
import { QueryDictionary, useNavigate, useQuery } from '../../routes';
import {
  useCalculatePrice,
  useGetQuotaAndCycleFromPathParams,
  useGetTrackingCookies,
  usePlanCartSnapshot,
} from '../../hook';
import { TermAndConditionCheck } from '../checkout/order-confirmation-section/TermAndConditionCheck';
import CouponSection from './component/CouponSection';
import {
  confirmPayment as confirmPaymentRoute,
  paymentStatus as paymentStatusRoute,
  plan as planRoute,
} from '../../layout/routes';
import {
  Feature,
  getFeatureByAddOn,
  getAddOnCodeByFeature,
  getProductId,
  OrderPreviewRequest,
  OrderStatus,
  PaymentMethodType,
  PlanInterval,
  SubscriptionSource,
} from '../../models';
import { useLaunchDarkly } from '../../launchdarkly';
import {
  DEFAULT_CURRENCY_FORMAT_OPTIONS,
  PARTNER_ID_COOKIE_NAME,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
  SUPPORTED_FEATURES,
  TRUE_TESTCLOUD_ADDON_FEATURES,
} from '../../utils/Constants';
import { Panel } from '../../components/common/Panel';
import { getRecurlySubUuIdFromPathParam, sendTrackingData } from '../../utils/utils';
import { withCartSnapshot } from '../../utils/analytics';
import {
  getTargetSelfServeSubscription,
  parseSubscriptionId,
} from '../../utils/subscriptionTarget';
import {
  buildPreviewProducts,
  getCloudSessionQuota,
  getFeatureQuota,
  getPaidAddOnLineItems,
  getPreviewExistingQuantity,
  getPreviewProductFeature,
  getTrueBundleTitleId,
  isMonthlyToAnnualSwitchRequest,
  isMonthlyToAnnualSwitchPreview,
} from '../plan/component/true-platform/trueBundlePreview';
import {
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PURCHASE_FEATURES,
} from '../plan/component/true-platform/trueBundlePlans';
import { getManualPurchaseProductCode } from '../plan/component/true-platform/trueBundleProductCode';

const useStyles = makeStyles(theme => ({
  planHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    borderBottom: '1px solid #e5e7eb',
  },
  orderSummaryCard: {
    padding: theme.spacing(0, 2, 2, 2),
    margin: theme.spacing(0, 2, 2, 2),
    backgroundColor: '#ffffff',
    height: 'fit-content',
    position: 'sticky',
  },
  orderSummaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  starterPricingBadge: {
    border: '1px solid #5959EB',
    color: '#5959EB',
    fontWeight: 700,
    fontSize: 10,
    height: 22,
    borderRadius: 4,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.75, 0),
  },
  summaryLabel: {
    fontWeight: 400,
    fontSize: 14,
    color: '#808B9A',
  },
  summaryValue: {
    fontWeight: 600,
    fontSize: 14,
    color: '#22283c',
  },
  summaryValueHighlight: {
    fontWeight: 700,
    fontSize: 14,
    color: '#5959EB',
  },
  productRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.4, 0),
    borderBottom: '1px solid #F0EFF6',
  },
  productName: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.35,
    color: '#22283c',
  },
  productQuantity: {
    fontSize: 13,
    lineHeight: 1.45,
    color: '#9CA3AF',
    marginTop: theme.spacing(0.25),
  },
  productAmount: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.35,
    color: '#22283c',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productOriginalAmount: {
    fontSize: 12.5,
    color: '#9CA3AF',
    textDecoration: 'line-through',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productTaxAmount: {
    fontSize: 12.5,
    color: '#808B9A',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productFreeAmount: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.35,
    color: '#16865F',
    textAlign: 'right',
  },
  discountValue: {
    fontWeight: 600,
    fontSize: 14,
    color: '#008060',
  },
  savingsBanner: {
    backgroundColor: '#EEF2FF',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    margin: theme.spacing(1.5, 0),
  },
  savingsBannerText: {
    fontWeight: 600,
    fontSize: 13,
    color: '#5959EB',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: theme.spacing(1),
  },
  totalLabel: {
    fontWeight: 700,
    fontSize: 16,
    color: '#22283c',
  },
  totalAmount: {
    fontWeight: 800,
    fontSize: 22,
    color: '#5959EB',
    textAlign: 'right',
  },
  billedLabel: {
    fontWeight: 400,
    fontSize: 12,
    color: '#808B9A',
    textAlign: 'right',
  },
  checkoutButton: {
    backgroundColor: '#5959EB',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 15,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#43509B',
    },
    '&:disabled': {
      backgroundColor: '#b0b0b0',
      color: '#ffffff',
    },
  },
  backLink: {
    display: 'block',
    textAlign: 'center',
    marginTop: theme.spacing(1.5),
    fontSize: 13,
    color: '#5959EB',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const getSummaryQuantityLabel = (
  feature: Feature,
  quantity: number,
) => {
  if (feature === Feature.PER_USER_KSE || feature === Feature.UNLIMITED_ENGINE) {
    return `${quantity} ${quantity === 1 ? 'license' : 'licenses'}`;
  }

  if (
    feature === Feature.TESTCLOUD_SESSION_CROSS_BROWSER
    || feature === Feature.TESTCLOUD_SESSION_MOBILE_APP
    || feature === Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE
    || feature === Feature.TESTCLOUD_SESSION_WEB_DESKTOP
  ) {
    return `${quantity} ${quantity === 1 ? 'session' : 'sessions'}`;
  }

  return `${quantity} ${quantity === 1 ? 'seat' : 'seats'}`;
};

export const OrderConfirmationPage = () => {
  const { get, queryString, queryDictionary } = useQuery();
  const { replace, replaceQuery, redirect } = useNavigate();
  const { flags } = useLaunchDarkly();
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const paymentInfoRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<'loading' | 'finished' | undefined>(undefined);
  const [cookies] = useCookies();

  const accountId = Number(get('accountId'));
  const featuresQuotaAndCycle = useGetQuotaAndCycleFromPathParams(SUPPORTED_FEATURES);

  // Store selectors
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  const orderResponse = useSelector(fromOrder.selectOrderResponse);
  const termsAndConditionsAccepted = useSelector(fromOrder.selectTermsAndConditionsAccepted);
  const couponName = useSelector(fromOrder.selectCouponName);
  const couponError = useSelector(fromOrder.selectCouponError);
  const appliedCoupon = useSelector(fromOrder.selectAppliedCoupon);
  const appliedPromotion = useSelector(fromOrder.selectAppliedPromotion);

  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);
  const currentCard = useSelector(fromPaymentMethod.selectOneByAccountId(accountId));
  const billingInfoLoading = useSelector(fromBillingInformation.selectLoading);
  const orderLoading = useSelector(fromOrder.selectLoading);
  const currentBillingInformation = useSelector(
    fromBillingInformation.selectOneByAccountId(accountId),
  );
  const isNotSyncedBillingInfo = useSelector(
    fromBillingInformation.isNotSyncedAddressByAccountId(accountId),
  ) ?? false;

  const cartSnapshot = usePlanCartSnapshot();
  const trackingCookies = useGetTrackingCookies();
  const checkoutError = useSelector(fromOrder.selectCheckoutError);
  const hasTrueSubscription = useSelector(
    fromSubscription.selectTrueSubscriptions(SubscriptionSource.RECURLY),
  );
  const currentSubscriptions = useSelector(fromSubscription.selectByAccountId(accountId));
  const recurlySubscriptions = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  );
  const hasPaidSubscription = useSelector(fromSubscription.hasPaidSubscription(accountId));

  const isNotUsingStripe = !flags?.usingStripeCheckoutSession && !flags?.usingStripeSetupIntent;
  const moreTestingPlansEnabled = flags?.feTrueBundleMoreTestingPlansEnabled === true;
  const legacyTrueTeamQuota = featuresQuotaAndCycle[Feature.TRUE_TEAM]?.quota ?? 0;
  const targetSubscriptionId = parseSubscriptionId(get(PLAN_SUBSCRIPTION_ID_QUERY_PARAM));

  const buildAddOns = (): Record<string, number> => {
    if (legacyTrueTeamQuota === 0) {
      return {};
    }

    const addOns: Record<string, number> = {};
    TRUE_TESTCLOUD_ADDON_FEATURES.forEach(feature => {
      const quota = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const addOnCode = getAddOnCodeByFeature(feature);
      if (quota > 0 && addOnCode) {
        addOns[addOnCode.toUpperCase()] = quota;
      }
    });
    return addOns;
  };

  // Billing cycle
  const getRecurlySubscriptionUuid = (feature: Feature) => getRecurlySubUuIdFromPathParam(feature)
    ?? getTargetSelfServeSubscription(
      recurlySubscriptions.filter(subscription => subscription.accountId === accountId),
      feature,
      targetSubscriptionId,
    )?.recurlySubUuId;

  const trueBundleProductRequests = TRUE_BUNDLE_PURCHASE_FEATURES
    .map(feature => {
      const quantity = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const cycle = featuresQuotaAndCycle[feature]?.cycle ?? PlanInterval.YEAR;
      const recurlySubscriptionUuid = getRecurlySubscriptionUuid(feature);
      return {
        productCode: getManualPurchaseProductCode(feature, PlanInterval.fromString(cycle)),
        quantity,
        ...(recurlySubscriptionUuid ? { recurlySubscriptionUuid } : {}),
      };
    })
    .filter(product => product.quantity > 0
      || isMonthlyToAnnualSwitchRequest(product, currentSubscriptions));
  const moreTestingProductRequests = moreTestingPlansEnabled
    ? TRUE_MORE_TESTING_PRODUCT_FEATURES
      .map(feature => {
        const quantity = featuresQuotaAndCycle[feature]?.quota ?? 0;
        const cycle = featuresQuotaAndCycle[feature]?.cycle ?? PlanInterval.YEAR;
        const recurlySubscriptionUuid = getRecurlySubscriptionUuid(feature);
        return {
          productCode: `${getProductId(feature)}_${
            PlanInterval.getPlanIntervalCode(PlanInterval.fromString(cycle))
          }`,
          quantity,
          ...(recurlySubscriptionUuid ? { recurlySubscriptionUuid } : {}),
        };
      })
      .filter(product => product.quantity > 0)
    : [];
  const legacyTrueTeamCycle = featuresQuotaAndCycle[Feature.TRUE_TEAM]?.cycle ?? PlanInterval.YEAR;
  const legacyRecurlySubscriptionUuid = getRecurlySubscriptionUuid(Feature.TRUE_TEAM);
  const previewProductsRequest = legacyTrueTeamQuota > 0
    ? [{
      productCode: `${getProductId(Feature.TRUE_TEAM)}_${
        PlanInterval.getPlanIntervalCode(PlanInterval.fromString(legacyTrueTeamCycle))
      }`,
      quantity: legacyTrueTeamQuota,
      ...(legacyRecurlySubscriptionUuid
        ? { recurlySubscriptionUuid: legacyRecurlySubscriptionUuid }
        : {}),
    }]
    : [
      ...trueBundleProductRequests,
      ...moreTestingProductRequests,
    ];
  const addOns = buildAddOns();
  const previewRequestProducts = previewProductsRequest.map((product, index) => ({
    ...product,
    ...(index === 0 && Object.keys(addOns).length > 0 ? { addOns } : {}),
  }));
  const trueBundleCycle = previewRequestProducts.length > 0
    ? PlanInterval.getIntervalFromPlanCode(previewRequestProducts[0].productCode)
    : PlanInterval.YEAR;
  const isAnnual = trueBundleCycle === PlanInterval.YEAR;
  const productsPreview = buildPreviewProducts(
    previewRequestProducts,
    orderPreviewResponse,
    flags?.subscriptionPlanPrice,
  );
  const selectedProducts = productsPreview.filter(product => product.newQuantity > 0
    || isMonthlyToAnnualSwitchPreview(product, currentSubscriptions));
  const hasSelectedProducts = selectedProducts.length > 0;
  const cloudQuota = getCloudSessionQuota(productsPreview, currentSubscriptions);
  const displaySubTotal = productsPreview.reduce((sum, product) => sum + product.subTotal, 0);
  const displayGrossTotal = productsPreview.reduce((sum, product) => sum + product.total, 0);
  const displayTax = productsPreview.reduce((sum, product) => sum + product.tax, 0);
  const discountAmount = productsPreview.reduce(
    (sum, product) => sum + product.discountAmount,
    0,
  );
  const monthlyToAnnualSwitchCredit = selectedProducts
    .filter(product => isMonthlyToAnnualSwitchPreview(product, currentSubscriptions))
    .reduce(
      (sum, product) => sum + product.creditAmount,
      0,
    );
  const displayTotal = displayGrossTotal - monthlyToAnnualSwitchCredit;

  const addOnLineItems = getPaidAddOnLineItems(productsPreview);
  const featureQuota: Partial<Record<Feature, number>> = getFeatureQuota(productsPreview);
  const total = displayTotal;
  const monthlyPrice = useCalculatePrice(
    flags?.subscriptionPlanPrice,
    featureQuota,
    PlanInterval.MONTH,
  );
  const annualSavings = (monthlyPrice * 12) - total;
  const buildPreviewRequest = (): OrderPreviewRequest => ({
    singleProductList: previewRequestProducts,
  });

  const buildCheckoutRequest = () => {
    const partnerId = cookies[PARTNER_ID_COOKIE_NAME];
    return {
      singleProductList: buildPreviewRequest().singleProductList,
      paymentMethod: selectedPaymentMethod,
      ...(couponName && { couponName }),
      ...(partnerId && { partnerId }),
    };
  };

  const checkoutDisabled = () => {
    if (isNotSyncedBillingInfo) return true;

    // do not check out when account do not have billing information
    if (!currentBillingInformation) {
      return true;
    }

    if (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER) {
      return false;
    }

    return !currentCard;
  };

  const isCheckoutDisabled = loading || previewLoading
    || !termsAndConditionsAccepted
    || !hasSelectedProducts
    || checkoutDisabled();

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethodType) => {
    dispatch(fromPaymentMethod.doChangeSelectedPaymentMethod(paymentMethod));
    if (PaymentMethodType.BANK_TRANSFER === paymentMethod) {
      dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(false));
    }
  };

  const handleCheckout = () => {
    const doCheckout = async () => {
      setLoading(true);
      setCheckoutStatus('loading');
    };

    doCheckout().catch(() => {
      // do nothing
    });
  };

  const handleBackToConfiguration = () => {
    replace(
      planRoute.path,
      replaceQuery(parse(queryString()) as QueryDictionary),
    );
  };

  // Effect: redirect to paymentStatus after successful checkout
  useEffect(() => {
    const redirectToPaymentStatusPage = () => {
      if (!orderResponse) return;
      replace(paymentStatusRoute.path, replaceQuery({
        ...queryDictionary(),
        accountId,
        orderId: orderResponse.id,
      }));
    };

    const performCheckout = async () => {
      await dispatch(fromOrder.doCheckout(buildCheckoutRequest()));
      setCheckoutStatus('finished');
    };

    if (checkoutStatus === 'loading' && currentBillingInformation
      && (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER
        || !isNotUsingStripe || currentCard)) {
      performCheckout().finally(() => setLoading(false));
    }

    if (checkoutStatus === 'finished') {
      if (selectedPaymentMethod === PaymentMethodType.CREDIT_CARD) {
        const isPendingOrder = orderResponse?.status === OrderStatus.PENDING;
        const shouldRedirectToStripeCheckout = isPendingOrder
          && orderResponse?.stripeInvoiceUrl
          && flags?.usingStripeCheckoutSession;
        const shouldConfirmPayment = isPendingOrder
          && orderResponse?.stripeClientSecret
          && flags?.usingStripeSetupIntent;

        if (shouldRedirectToStripeCheckout) {
          redirect(orderResponse!.stripeInvoiceUrl!);
        } else if (shouldConfirmPayment) {
          replace(confirmPaymentRoute.path, replaceQuery({
            accountId,
            orderId: orderResponse!.id.toString(),
          }));
        } else {
          redirectToPaymentStatusPage();
        }
      }

      if (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER) {
        replace(planRoute.path, replaceQuery({ accountId, showInstructionPopup: true }));
      }
    }
  }, [checkoutStatus, currentBillingInformation, currentCard]);

  const fetchInfo = async () => {
    if (!accountId) return;
    await Promise.all([
      dispatch(fromOrder.resetTermsAndConditionsAccepted()),
      dispatch(fromAccounts.doCheckPublicDomain(accountId)),
      dispatch(fromOrder.resetOrderPreviewRequest()),
      dispatch(fromSubscription.doFetchSubscriptions({ accountId })),
      dispatch(fromOrder.doFetchAllOrder(accountId)),
      dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
      dispatch(fromAccounts.doCheckFirstThreeLicenses()),
    ]);
  };

  const sendCheckoutClickTracking = ({
    error,
    transactionId,
    invoiceNumbers,
  }: {
    error?: string;
    transactionId?: string | number;
    invoiceNumbers?: string[];
  } = {}) => {
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      withCartSnapshot({
        field: 'order_confirmation',
        object: 'button',
        action: 'checkout',
        plan_flow: legacyTrueTeamQuota > 0 ? 'true_team_legacy' : 'true_bundle',
        is_required: false,
        is_valid: !error,
        error,
        ...trackingCookies,
        ...(transactionId ? { transaction_id: transactionId } : {}),
        ...(invoiceNumbers && invoiceNumbers.length > 0 ? { invoice_numbers: invoiceNumbers } : {}),
      }, cartSnapshot),
    );
  };

  useEffect(() => {
    if (checkoutStatus !== 'finished') {
      // Action doCheckout - not dispatch yet
      return;
    }
    const hasCheckoutResult = !!checkoutError || !!orderResponse;
    if (!hasCheckoutResult) {
      return;
    }

    const isSuccessCheckout = !!orderResponse;
    if (isSuccessCheckout) {
      const invoiceNumbers = orderResponse?.singleProductInvoices;
      const transactionId = orderResponse?.id;

      sendCheckoutClickTracking({
        transactionId,
        invoiceNumbers,
      });
    } else {
      sendCheckoutClickTracking({
        error: checkoutError?.message,
      });
    }
  }, [checkoutError, orderResponse, checkoutStatus]);

  // Fetch order preview whenever quotas/cycle change
  useEffect(() => {
    if (!accountId || loading || billingInfoLoading || orderLoading) return;
    dispatch(fromOrder.doPreviewOrder(buildPreviewRequest()));
  }, [currentBillingInformation]);

  const renderOrderSummary = () => (
    <Paper className={classes.orderSummaryCard} elevation={0}>
      {/* Line items */}
      <Box mt={1.5}>
        <Box mt={1.5}>
          {selectedProducts.map(product => {
            const feature = getPreviewProductFeature(product);
            const isSwitchToAnnual = isMonthlyToAnnualSwitchPreview(
              product,
              currentSubscriptions,
            );
            const quantity = isSwitchToAnnual ? product.quantity : product.newQuantity;
            const productAmountBeforeTax = Math.max(
              product.subTotal - product.discountAmount,
              0,
            );

            return (
              <Box className={classes.productRow} key={product.productCode}>
                <Box minWidth={0}>
                  <Typography className={classes.productName}>
                    <FormattedMessage id={getTrueBundleTitleId(feature)} />
                  </Typography>
                  <Typography className={classes.productQuantity}>
                    {isSwitchToAnnual
                      ? intl.formatMessage(
                        {
                          id: 'page.plan.v2.order.summary.seats.total.value',
                        },
                        {
                          count: quantity,
                        },
                      )
                      : getSummaryQuantityLabel(feature, quantity)}
                  </Typography>
                  {isSwitchToAnnual && (
                    <Typography className={classes.productQuantity}>
                      <FormattedMessage
                        id={product.newQuantity > 0
                          ? 'page.plan.v2.order.summary.includes.current.new.seats'
                          : 'page.plan.v2.order.summary.includes.current.seats'}
                        values={{
                          currentCount: getPreviewExistingQuantity(product),
                          newCount: product.newQuantity,
                        }}
                      />
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography className={classes.productAmount}>
                    {formatCurrency(
                      intl,
                      productAmountBeforeTax,
                      DEFAULT_CURRENCY_FORMAT_OPTIONS,
                    )}
                  </Typography>
                  {product.subTotal > productAmountBeforeTax && (
                    <Typography className={classes.productOriginalAmount}>
                      {formatCurrency(intl, product.subTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                    </Typography>
                  )}
                  {product.tax > 0 && (
                    <Typography className={classes.productTaxAmount}>
                      <FormattedMessage
                        id="page.plan.v2.order.summary.product.tax"
                        values={{
                          amount: formatCurrency(
                            intl,
                            product.tax,
                            DEFAULT_CURRENCY_FORMAT_OPTIONS,
                          ),
                        }}
                      />
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
          <Box className={classes.summaryRow}>
            <Typography className={classes.billedLabel}>
              <FormattedMessage
                id={isAnnual
                  ? 'page.plan.v2.order.summary.billed.annually'
                  : 'page.plan.v2.order.summary.billed.monthly'}
              />
            </Typography>
          </Box>
        </Box>
      </Box>

      {(cloudQuota > 0 || addOnLineItems.length > 0) && (
        <>
          {cloudQuota > 0 && (
            <Box className={classes.productRow}>
              <Box minWidth={0}>
                <Typography className={classes.productName}>
                  <FormattedMessage id="page.plan.v2.order.summary.cloud.sessions" />
                </Typography>
                <Typography className={classes.productQuantity}>
                  {getSummaryQuantityLabel(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, cloudQuota)}
                </Typography>
              </Box>
              <Typography className={classes.productFreeAmount}>
                <FormattedMessage id="page.plan.v2.order.summary.free" />
              </Typography>
            </Box>
          )}
          {addOnLineItems.map(lineItem => (
            <Box
              key={lineItem.addOnCode}
              className={classes.productRow}
            >
              <Box minWidth={0}>
                <Typography className={classes.productName}>
                  <FormattedMessage id={`addon.${lineItem.addOnCode}.display.name`} />
                </Typography>
                <Typography className={classes.productQuantity}>
                  {getSummaryQuantityLabel(
                    getFeatureByAddOn(lineItem.addOnCode.toLowerCase()),
                    lineItem.quantity,
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography className={classes.productAmount}>
                  {formatCurrency(
                    intl,
                    Math.max(lineItem.subtotal - lineItem.discountAmount, 0),
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </Typography>
                {lineItem.discountAmount > 0 && (
                  <Typography className={classes.productOriginalAmount}>
                    {formatCurrency(intl, lineItem.subtotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                  </Typography>
                )}
                {lineItem.tax > 0 && (
                  <Typography className={classes.productTaxAmount}>
                    <FormattedMessage
                      id="page.plan.v2.order.summary.product.tax"
                      values={{
                        amount: formatCurrency(intl, lineItem.tax, DEFAULT_CURRENCY_FORMAT_OPTIONS),
                      }}
                    />
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </>
      )}

      <Box className={classes.summaryRow} pt={2.5} pb={1}>
        <Typography fontWeight={400} fontSize={14}>
          <FormattedMessage id="page.plan.v2.order.summary.standard.price" />
        </Typography>
        <Typography fontWeight={700} fontSize={14}>
          {formatCurrency(intl, displaySubTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
        </Typography>
      </Box>
      {appliedPromotion
        ? (
          <Box className={classes.summaryRow} pt={1} pb={4}>
            <Typography fontWeight={400} fontSize={14}>
              <FormattedMessage
                id="page.plan.v2.order.summary.promotion.discount"
                values={{
                  promotionCode: intl.formatMessage({
                    id: `promotion.${appliedPromotion?.campaignCode.toLowerCase()}.desc`,
                    defaultMessage: '',
                  }),
                }}
              />
            </Typography>
            <Typography fontWeight={700} fontSize={14}>
              {formatCurrency(
                intl,
                appliedPromotion?.discountAmount ?? 0,
                DEFAULT_CURRENCY_FORMAT_OPTIONS,
              )}
            </Typography>
          </Box>
        )
        : (
          <Box className={classes.summaryRow} pt={1} pb={4}>
            <Typography fontWeight={400} fontSize={14}>
              <FormattedMessage id="page.plan.v2.order.summary.discount" />
            </Typography>
            <Typography fontWeight={700} fontSize={14}>
              {formatCurrency(intl, discountAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
            </Typography>
          </Box>
        )}
      {appliedCoupon && (
        <Box className={classes.summaryRow} pt={1} pb={4}>
          <Typography fontWeight={400} fontSize={14}>
            <FormattedMessage
              id="page.plan.v2.order.summary.coupon.discount"
              values={{
                couponCode: appliedCoupon.code,
              }}
            />
          </Typography>
          <Typography fontWeight={700} fontSize={14}>
            {formatCurrency(
              intl,
              appliedCoupon?.appliedAmount ?? 0,
              DEFAULT_CURRENCY_FORMAT_OPTIONS,
            )}
          </Typography>
        </Box>
      )}
      {monthlyToAnnualSwitchCredit > 0 && (
        <Box className={classes.summaryRow} pt={1} pb={4}>
          <Typography fontWeight={400} fontSize={14}>
            <FormattedMessage id="page.plan.v2.order.summary.monthly.credit" />
          </Typography>
          <Typography fontWeight={700} fontSize={14} color="#008060">
            -
            {formatCurrency(intl, monthlyToAnnualSwitchCredit, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
          </Typography>
        </Box>
      )}
      {monthlyToAnnualSwitchCredit > 0 && (
        <Alert
          severity="info"
          sx={theme => ({
            marginBottom: theme.spacing(2),
            '& .MuiAlert-message': {
              fontSize: 12,
            },
          })}
        >
          <FormattedMessage id="page.plan.v2.order.summary.monthly.switch.note" />
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Pricing breakdown */}
      <Box>
        <Box className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>
            <FormattedMessage id="checkout.subtotal" />
          </Typography>
          <Typography className={classes.summaryValue}>
            {formatCurrency(
              intl,
              displayTotal - displayTax,
              DEFAULT_CURRENCY_FORMAT_OPTIONS,
            )}
          </Typography>
        </Box>
        <Box className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>
            <FormattedMessage id="page.order.confirmation.tax" />
          </Typography>
          <Typography className={classes.summaryValue}>
            {formatCurrency(intl, displayTax, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Annual savings banner – only when no current TRUE_TEAM/TRUE_ENTERPRISE subscription */}
      {isAnnual && discountAmount > 0 && !hasTrueSubscription && (
        <Box className={classes.savingsBanner}>
          <Typography className={classes.savingsBannerText}>
            🎉&nbsp;
            <FormattedMessage
              id="page.order.confirmation.annual.savings"
              values={{
                amount: formatCurrency(intl, annualSavings, DEFAULT_CURRENCY_FORMAT_OPTIONS),
              }}
            />
          </Typography>
        </Box>
      )}

      {/* Coupon section */}
      <CouponSection
        onApply={(name: string) => {
          dispatch(fromOrder.setCouponName(name));
          dispatch(fromOrder.doPreviewOrder({
            ...buildPreviewRequest(),
            couponName: name,
          }));
        }}
        onRemove={() => {
          dispatch(fromOrder.clearCoupon());
          dispatch(fromOrder.doPreviewOrder(buildPreviewRequest()));
        }}
        isLoading={previewLoading}
        appliedCoupon={appliedCoupon}
        error={couponError}
        isAnnual={isAnnual}
      />

      {/* Total */}
      <Box className={classes.totalRow}>
        <Typography className={classes.totalLabel}>
          <FormattedMessage id="page.plan.v2.order.summary.total" />
        </Typography>
        <Box textAlign="right">
          <Typography className={classes.totalAmount}>
            {formatCurrency(intl, displayTotal, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
          </Typography>
          <Typography className={classes.billedLabel}>
            <FormattedMessage
              id={isAnnual
                ? 'page.plan.v2.order.summary.billed.annually'
                : 'page.plan.v2.order.summary.billed.monthly'}
            />
          </Typography>
        </Box>
      </Box>

      {/* Terms and conditions */}
      <Box
        mt={2}
        sx={{
          backgroundColor: '#F3F1FF',
        }}
        p={2}
      >
        <TermAndConditionCheck />
      </Box>

      {/* Checkout button */}
      <Button
        fullWidth
        variant="contained"
        className={classes.checkoutButton}
        onClick={handleCheckout}
        disabled={isCheckoutDisabled}
      >
        <FormattedMessage id="billinginfo.checkout" />
      </Button>

      {/* Back to configuration */}
      <Box
        id="back-to-configuration"
        key="back-to-configuration"
        display="flex"
        alignItems="center"
        alignContent="center"
        justifyContent="center"
      >
        <Button
          variant="text"
          onClick={handleBackToConfiguration}
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Typography
            variant="body1"
            fontWeight={500}
            fontSize={14}
            color="#5959EB"
            sx={{
              textDecoration: 'underline',
            }}
          >
            <FormattedMessage id="page.order.confirmation.back.to.configuration" />
          </Typography>
        </Button>
      </Box>
    </Paper>
  );

  return (
    <Page
      id="order-confirmation"
      headerId="page.order.confirmation.header"
      fetchData={fetchInfo}
    >
      <Box
        id="plan-v2-content"
        key="plan-v2-content"
        display="flex"
        gap={3}
        py={3}
        sx={theme => ({
          flexDirection: 'row',
          [theme.breakpoints.down('tabletL')]: {
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        })}
      >
        {/* Left panel: payment information */}
        <Panel
          id="order-confirmation-left"
          step={2}
          headerLabel={
            <FormattedMessage id="page.order.confirmation.payment.billing" />
          }
          sx={{
            width: '100%',
            minWidth: '70%',
            maxWidth: 1200,
            pr: 4,
            pb: 4,
            borderRight: '1px solid #BBBBBB',
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            height="95%"
            overflow="auto"
            ml={4}
            mb={2}
          >
            <PaymentInformationSection
              ref={paymentInfoRef}
              accountId={accountId}
              enableSave
              availableRenewal
              hasPaidSubscription={hasPaidSubscription}
              handleSelectPaymentMethod={handleSelectPaymentMethod}
              showCardInformation={isNotUsingStripe}
            />
          </Box>
        </Panel>

        {/* Right panel: order summary */}
        <Panel
          id="order-confirmation-right"
          headerLabel={(
            <Box className={classes.orderSummaryHeader}>
              <FormattedMessage id="page.plan.v2.order.summary.title" />
            </Box>
          )}
          sx={theme => ({
            width: '30%',
            flexShrink: 0,
            [theme.breakpoints.down('tabletL')]: {
              width: '100%',
            },
          })}
          skipTransform
        >
          {renderOrderSummary()}
        </Panel>
      </Box>
      {(loading || previewLoading) && <LoadingProgress fullscreen />}
    </Page>
  );
};
